import { Capacitor } from '@capacitor/core';

/**
 * Source de vérité pour la version : version.json
 * Stockage local : app_version uniquement (VersionInfo après chargement ou mise à jour).
 */
export interface VersionInfo {
  version: string;
  buildHash: string;
  buildDate: string;
  buildNumber: number;
}

const OTA_CONFIG = {
  baseUrl: ((import.meta as any).env?.VITE_OTA_BASE_URL as string) || 'https://eservices.tn',
  versionPath: '/version.json',
  checkIntervalMs: 30 * 60 * 1000, // 30 minutes
  minIntervalBetweenChecks: 60 * 1000, // 1 minute (éviter doublons)
  reloadDelayMs: 1200,
  /** Timeout des requêtes version.json pour ne pas bloquer l'app en cas de serveur lent. */
  fetchTimeoutMs: 10 * 1000,
  /** Délai de base (ms) pour le backoff exponentiel après échec. */
  backoffBaseMs: 60 * 1000,
  /** Nombre max d'échecs consécutifs avant de plafonner le backoff. */
  backoffMaxAttempts: 5,
};

/**
 * Retourne l'URL à appeler pour récupérer version.json.
 * En développement : chemin relatif /ota/version.json (proxifié par Vite vers eservices.tn).
 * En production : URL complète (même origine ou VITE_OTA_BASE_URL).
 */
export function getVersionJsonUrl(): string {
  if (import.meta.env.DEV) {
    return '/ota/version.json';
  }
  return `${OTA_CONFIG.baseUrl}${OTA_CONFIG.versionPath}`;
}

class LiveUpdateService {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastCheckTime = 0;
  private isChecking = false;
  /** Nombre d'échecs consécutifs pour le backoff exponentiel. */
  private consecutiveFailures = 0;

  /**
   * Récupère la version distante (network-only).
   * Timeout + try/catch : en cas d'erreur (CORS, 500, réseau), retourne null sans faire planter l'app.
   */
  private async fetchRemoteVersion(): Promise<VersionInfo | null> {
    const baseUrl = getVersionJsonUrl();
    const url = `${baseUrl}?t=${Date.now()}`;

    let controller: AbortController | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      // Timeout : AbortSignal.timeout si disponible, sinon fallback manuel
      const timeoutMs = OTA_CONFIG.fetchTimeoutMs;
      let signal: AbortSignal | undefined;

      if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
        signal = (AbortSignal as any).timeout(timeoutMs);
      } else {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller!.abort(), timeoutMs);
        signal = controller.signal;
      }

      const res = await fetch(url, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        signal,
      });

      if (!res.ok) {
        console.warn('[liveUpdateService] version.json non OK:', res.status, res.statusText);
        return null;
      }

      const data = (await res.json()) as VersionInfo;
      if (data && typeof data.buildNumber === 'number') {
        return data;
      }
      console.warn('[liveUpdateService] version.json invalide (buildNumber manquant)');
      return null;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isAbort = message.includes('abort') || (err instanceof Error && err.name === 'AbortError');
      console.warn(
        '[liveUpdateService] Échec récupération version:',
        isAbort ? 'timeout ou annulé' : message
      );
      return null;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  /**
   * Compare les versions par buildNumber. Retourne true si une mise à jour est disponible.
   */
  private hasNewVersion(remote: VersionInfo): boolean {
    const localStr = localStorage.getItem('app_version');
    if (!localStr) return true;
    try {
      const local = JSON.parse(localStr) as VersionInfo;
      return remote.buildNumber > local.buildNumber;
    } catch {
      return true;
    }
  }

  /**
   * Vérifie si une nouvelle version est disponible. Si oui : enregistre la version et recharge l’app (reload simple).
   * Une seule source de vérification OTA : ce service (pas de duplication dans SplashScreen).
   */
  /**
   * Délai minimum avant la prochaine tentative après des échecs consécutifs (backoff exponentiel).
   */
  private getBackoffMs(): number {
    if (this.consecutiveFailures === 0) return OTA_CONFIG.minIntervalBetweenChecks;
    const exponent = Math.min(this.consecutiveFailures - 1, OTA_CONFIG.backoffMaxAttempts);
    return OTA_CONFIG.backoffBaseMs * Math.pow(2, exponent);
  }

  /**
   * Vérifie si une nouvelle version est disponible. Si oui : enregistre la version et recharge l'app (reload simple).
   * Une seule source de vérification OTA : ce service (pas de duplication dans SplashScreen).
   * En cas d'échec (500, CORS, timeout), backoff exponentiel pour éviter les requêtes infinies.
   */
  async checkForUpdates(force = false): Promise<boolean> {
    const now = Date.now();
    const backoffMs = this.getBackoffMs();
    if (!force && now - this.lastCheckTime < backoffMs) return false;
    if (this.isChecking) return false;

    this.isChecking = true;
    this.lastCheckTime = now;

    try {
      const remote = await this.fetchRemoteVersion();
      if (!remote) {
        this.consecutiveFailures++;
        return false;
      }

      this.consecutiveFailures = 0;

      const localStr = localStorage.getItem('app_version');
      if (!localStr) {
        // Premier chargement : enregistrer la version sans recharger (évite reload en boucle).
        localStorage.setItem('app_version', JSON.stringify(remote));
        return false;
      }

      if (!this.hasNewVersion(remote)) return false;

      localStorage.setItem('app_version', JSON.stringify(remote));
      this.showUpdateNotification(remote);
      setTimeout(() => window.location.reload(), OTA_CONFIG.reloadDelayMs);
      return true;
    } catch (err) {
      this.consecutiveFailures++;
      console.warn('[liveUpdateService] checkForUpdates erreur:', err);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  private showUpdateNotification(version: VersionInfo): void {
    try {
      const el = document.createElement('div');
      el.style.cssText = `
        position:fixed;top:20px;left:50%;transform:translateX(-50%);
        background:#12b8ff;color:#fff;padding:12px 24px;border-radius:8px;
        box-shadow:0 4px 12px rgba(0,0,0,0.15);z-index:10000;
        font-family:system-ui,sans-serif;font-size:14px;font-weight:600;
      `;
      el.textContent = `Mise à jour (Build ${version.buildNumber})…`;
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 2500);
    } catch {}
  }

  /**
   * Initialise le service OTA : une vérification au lancement (après splash), puis toutes les 30 min.
   * Sur le web : permet d'obtenir la bonne version des assets et d'éviter les liens SPA cassés (reload si version distante plus récente).
   * Pas de flags _ota_force_reload / _ota_use_remote. Reload simple en cas de nouvelle version.
   */
  initialize(): void {
    const afterSplashMs = Capacitor.isNativePlatform() ? 2500 : 1500;
    setTimeout(() => this.checkForUpdates(true), afterSplashMs);

    this.intervalId = setInterval(() => this.checkForUpdates(), OTA_CONFIG.checkIntervalMs);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  getCurrentVersion(): VersionInfo | null {
    try {
      const s = localStorage.getItem('app_version');
      return s ? (JSON.parse(s) as VersionInfo) : null;
    } catch {
      return null;
    }
  }

  async forceCheck(): Promise<boolean> {
    return this.checkForUpdates(true);
  }

  resetVersion(): void {
    localStorage.removeItem('app_version');
  }
}

export const liveUpdateService = new LiveUpdateService();
